import React, { useEffect } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { registrationSchema, RegistrationSchema } from './schema'
import { useDistrictList, useRegister } from './api'
import FormBuilder from '../../components/app/formBuilder'
import { useNavigate } from 'react-router-dom'

const Registration: React.FC = () => {
  const methods = useForm<RegistrationSchema>({
    resolver: zodResolver(registrationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      mobile: '',
      whatsapp_choice: '',
      whatsapp_no: '',
      role: '',
      district: '',
      district_id: '',
      password: '',
      password_confirmation: '',
      background: '',
    },
  })

  const navigate = useNavigate()
  const { data } = useDistrictList()

  const whatsappChoice = useWatch({
    control: methods.control,
    name: 'whatsapp_choice',
  })
  const mobileNumber = useWatch({
    control: methods.control,
    name: 'mobile',
  })

  useEffect(() => {
    if (whatsappChoice === 'No' && mobileNumber) {
      methods.setValue('whatsapp_no', '')
    }
  }, [whatsappChoice, methods])

  const onSuccess = (data: any) => {
    console.log('Registration successful:', data)
    methods.reset()
    navigate('/')
  }

  const { mutate: registerUser, isPending } = useRegister(onSuccess)

  const onSubmit = (data: RegistrationSchema) => {
    // Extract values
    const { whatsapp_choice, district_id, ...rest } = data

    // Handle whatsapp number
    const whatsapp_no =
      whatsapp_choice === 'Yes' ? data.mobile : data.whatsapp_no

    // Final payload with district_id
    const payload = {
      ...rest,
      whatsapp_no,
      district_id: district_id, // convert district → district_id
    }

    registerUser(payload)
  }

  console.log(methods.formState.errors)

  const personalInformation = [
    {
      name: 'name' as const,
      label: 'Name of Applicant',
      type: 'text' as const,
      required: true,
      disabled: false,
    },
    {
      name: 'email' as const,
      label: 'Email address',
      type: 'email' as const,
      required: true,
      disabled: false,
    },
    {
      name: 'mobile' as const,
      label: 'Phone Number',
      type: 'number' as const,
      required: true,
      disabled: false,
    },
    {
      name: 'whatsapp_choice' as const,
      label: 'Is this your WhatsApp Number?',
      type: 'radio' as const,
      required: true,
      disabled: false,
      data: [
        { id: 1, radioLabel: 'Yes', value: 'Yes' },
        { id: 2, radioLabel: 'No', value: 'No' },
      ],
    },
    ...(whatsappChoice === 'No'
      ? [
          {
            name: 'whatsapp_no' as const,
            label: 'WhatsApp Number',
            type: 'number' as const,
            required: true,
            disabled: false,
          },
        ]
      : []),

    {
      name: 'role_name' as const,
      label: 'Role',
      type: 'custom_select' as const,
      placeholder: 'Select Role',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      id: 'role',
      data: [
        { name: 'Aspiring Entrepreneur', id: '2' },
        { name: 'Schools And Colleges', id: '3' },
        { name: 'Existing Entrepreneurs', id: '4' },
        { name: 'Research Institutions And Universities', id: '5' },
        { name: 'Industry Associations / Enablers', id: '6' },
        { name: 'Experts / Mentor', id: '7' },
        { name: 'Ed Club', id: '8' },
        { name: 'Students', id: '9' },
        { name: 'Guest', id: '10' },
      ],
    },
    {
      name: 'district' as const,
      id: 'district_id',
      label: 'District',
      type: 'custom_select' as const,
      placeholder: 'Select District',
      required: true,
      disabled: false,
      desc: 'name',
      descId: 'id',
      data: data?.data?.map((item: any) => ({
        id: item.id,
        name: item?.district_institution ?? item?.district_name ?? item?.name,
      })),
    },
    {
      name: 'password' as const,
      label: 'Password',
      type: 'password' as const,
      required: true,
      disabled: false,
    },
    {
      name: 'password_confirmation' as const,
      label: 'Confirm Password',
      type: 'password' as const,
      required: true,
      disabled: false,
    },
  ]

  console.log(methods.watch())
  console.log(methods.formState.errors)

  return (
    <form
      onSubmit={methods.handleSubmit(onSubmit)}
      className="min-h-screen flex items-center justify-center bg-gray-50 px-5 py-4"
    >
      <FormProvider {...methods}>
        <div className="max-w-xl w-full bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-3xl font-semibold text-center mb-2">Register</h2>
          <p className="text-center text-gray-500 mb-8">
            Get started with your account.
          </p>

          <div className="grid grid-cols-1 gap-4">
            <FormBuilder data={personalInformation} edit />

            {Object.keys(methods.formState.errors).length > 0 && (
              <div className="text-red-500 text-sm mt-2">
                {Object.entries(methods.formState.errors).map(
                  ([key, error]) => (
                    <p key={key}>{error.message?.toString()}</p>
                  )
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={
              isPending || Object.keys(methods.formState.errors).length > 0
            }
            className={`w-full ${
              isPending || Object.keys(methods.formState.errors).length > 0
                ? 'bg-orange-300 cursor-not-allowed'
                : 'bg-orange-500 hover:bg-orange-600'
            } text-white font-semibold py-2 rounded transition mt-6`}
          >
            {isPending ? 'Registering...' : 'Register'}
          </button>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already Have An Account?
            <a
              href="/login"
              className="text-orange-500 hover:underline font-medium ml-1"
            >
              Sign In
            </a>
          </p>
        </div>
      </FormProvider>
    </form>
  )
}

export default Registration
